/**
 * Patient Portal Layout
 *
 * Simplified layout for patient portal with limited navigation
 */

import { Outlet, useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'

import { APP_ROUTES } from '@/config/constants'
import { env } from '@/config/env'
import { Button } from '@/presentation/components/ui/Button'
import { HeaderLogo } from '@/presentation/components/ui/AppLogo'
import { patientLogout, getStoredPatientInfo } from '@/application/auth/auth.service'

// =============================================================================
// Types
// =============================================================================

interface PatientInfo {
  id: string
  firstName: string
  lastName: string
  documentType: string
  documentNumber: string
  email: string | null
}

// =============================================================================
// Component
// =============================================================================

export function PatientPortalLayout() {
  const navigate = useNavigate()

  // Get patient info from separate patient storage
  const patientInfo = getStoredPatientInfo() as PatientInfo | null

  const handleLogout = () => {
    // Only clears patient tokens, does NOT affect staff session
    patientLogout()
    navigate(APP_ROUTES.PATIENT_LOGIN)
  }

  return (
    <div className="min-h-screen alto-de-pantalla bg-gradient-to-br from-gray-50 to-teal-50">
      {/* Header - Hidden during print */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200 dark:border-gray-800 shadow-sm print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <HeaderLogo subtitle="Portal del Paciente" />

            {/* Sin barra de navegacion: el portal es UNA sola pantalla.
                Antes habia dos enlaces, "Mi Portal" y "Mis Estudios", que
                llevaban al mismo contenido. Ofrecer dos caminos al mismo
                sitio es de lo mas confuso para quien navega poco: se queda
                decidiendo cual es el correcto. */}

            {/* User Menu */}
            <div className="flex items-center gap-4">
              {patientInfo && (
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {patientInfo.firstName} {patientInfo.lastName}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {patientInfo.documentType}: {patientInfo.documentNumber}
                  </p>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Salir
              </Button>
            </div>
          </div>
        </div>

      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Footer - Hidden during print */}
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-white mt-auto print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} {env.APP_NAME}. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  )
}
